import { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useEngine } from '@/app/EngineContext';
import { useSettingsStore } from '@/store/settingsStore';
import { useCameraStore } from '@/store/cameraStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useInteractionStore } from '@/store/interactionStore';
import { cullVisible } from '@/core/rendering/culling';
import { IconButton } from '@/components/ui';
import styles from './Chrome.module.css';

interface Snapshot {
  readonly objects: number;
  readonly visible: number;
  readonly selected: number;
  readonly connections: number;
  readonly viewport: string;
  readonly camera: string;
  readonly zoom: string;
  readonly mode: string;
  readonly hovered: string;
  readonly history: string;
  readonly index: string;
  readonly sync: string;
  readonly frame: string;
}

/**
 * Developer mode (⇧⌘D): live engine diagnostics. Every number is read from the real
 * stores and engine — nothing is estimated. Sampled at 4 Hz so the panel itself is
 * not a measurable cost; frame interval is the mean rAF cadence over the last second.
 */
export const DeveloperPanel = () => {
  const enabled = useSettingsStore((s) => s.developerMode);
  return enabled ? <Panel /> : null;
};

const Panel = () => {
  const engine = useEngine();
  const [snap, setSnap] = useState<Snapshot | null>(null);

  useEffect(() => {
    let frames = 0;
    let last = performance.now();
    let meanFrameMs = 0;
    let raf = 0;
    const tick = (now: number) => {
      frames++;
      if (now - last >= 1000) {
        meanFrameMs = (now - last) / frames;
        frames = 0;
        last = now;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const sample = () => {
      const ws = useWorkspaceStore.getState();
      const doc = ws.workspaces[ws.activeWorkspaceId];
      const cam = useCameraStore.getState();
      const sel = useSelectionStore.getState().selection;
      const ia = useInteractionStore.getState();
      const hist = ws.histories[ws.activeWorkspaceId];
      const objects = doc ? Object.keys(doc.objects).length : 0;
      const visible = cam.viewport.size.width ? cullVisible(engine.scene, cam.camera, cam.viewport.size).length : 0;
      const idx = engine.scene.indexStats();
      const d = engine.diagnostics();
      setSnap({
        objects,
        visible,
        selected: sel.objectIds.length,
        connections: doc ? Object.keys(doc.connections).length : 0,
        viewport: `${cam.viewport.size.width}×${cam.viewport.size.height} @${cam.viewport.devicePixelRatio}x`,
        camera: `${cam.camera.position.x.toFixed(1)}, ${cam.camera.position.y.toFixed(1)}`,
        zoom: `${(cam.camera.zoom * 100).toFixed(1)}%`,
        mode: ia.mode,
        hovered: ia.hoveredId ?? '—',
        history: `${hist?.past.length ?? 0} past · ${hist?.future.length ?? 0} future`,
        index: `${idx.nodes} nodes · ${idx.leaves} leaves · depth ${idx.maxDepth} · ${engine.scene.size} items`,
        sync: `${d.lastSyncMs.toFixed(2)} ms · ${d.lastSyncSize} objects · ${d.syncCount} syncs`,
        frame: meanFrameMs ? `${meanFrameMs.toFixed(1)} ms (${(1000 / meanFrameMs).toFixed(0)} fps)` : 'measuring…',
      });
    };
    sample();
    const timer = setInterval(sample, 250);
    return () => {
      clearInterval(timer);
      cancelAnimationFrame(raf);
    };
  }, [engine]);

  if (!snap) return null;
  const rows: [string, string | number][] = [
    ['objects', snap.objects],
    ['visible', snap.visible],
    ['culled', snap.objects - snap.visible],
    ['selected', snap.selected],
    ['connections', snap.connections],
    ['viewport', snap.viewport],
    ['camera', snap.camera],
    ['zoom', snap.zoom],
    ['interaction', snap.mode],
    ['hovered', snap.hovered],
    ['history', snap.history],
    ['quadtree', snap.index],
    ['scene sync', snap.sync],
    ['frame', snap.frame],
  ];
  return (
    <Paper
      className={styles.developer}
      aria-label="Developer mode"
      data-testid="developer-panel"
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
    >
      <div className={styles.developerHeader}>
        <Typography variant="overline" color="text.disabled">
          Developer
        </Typography>
        <IconButton
          label="Close developer mode"
          shortcut="⇧⌘D"
          onClick={() => {
            useSettingsStore.getState().setDeveloperMode(false);
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </div>
      <dl className={styles.developerList}>
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd data-testid={`dev-${k.replace(/\s/g, '-')}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </Paper>
  );
};
